import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mutator } from '@/shared/api';
import { handleSuccess } from '@/shared/lib/success';
import { superCategoryKeys } from './queryKeys';

interface SuperCategoryFields {
  name: string;
  slug: string;
  parentId?: string | null;
  status?: 'ACTIVE' | 'INACTIVE';
  priority?: number;
  image?: string;
}

/**
 * `CreateCatalogSuperCategoryTreeDto` requires BOTH `name` and `slug`.
 *
 * `slug` was optional here, and the tree panel's inline "Add Subcategory" duly
 * omitted it — so every add returned 400 "slug must be a string" with nothing in
 * the type system to catch it. Required now: a caller that forgets does not
 * compile.
 */
export type SuperCategoryCreatePayload = SuperCategoryFields;

/** `UpdateCatalogSuperCategoryDto` requires nothing — a PATCH may carry one
 *  field, which is how the inline rename sends just `{ name }`. */
export type SuperCategoryUpdatePayload = Partial<SuperCategoryFields>;

/**
 * `batch` hands BOTH the toast and the cache invalidation to the caller.
 *
 * One logical action can be several calls here: adding a child is a create plus
 * a PATCH to set its parent, and the edit page's Save commits a whole queue of
 * them. Per-call defaults get this wrong twice over — the toasts report
 * "created" then "updated" for what the admin did once, and each invalidation
 * refetches the tree mid-batch, so half-written state lands on screen and the
 * rows shift under the cursor on every call.
 *
 * A batch caller must invalidate `superCategoryKeys.all` when it finishes, or
 * the screen keeps showing pre-write data.
 */
interface MutationOptions {
  batch?: boolean;
}

export function useSystemSuperCategoryCreate({ batch }: MutationOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SuperCategoryCreatePayload) =>
      mutator('POST', '/category-variant/super-categories', data),
    onSuccess: () => {
      if (batch) return;
      handleSuccess(null, { titleKey: 'success.superCategory.created' });
      queryClient.invalidateQueries({ queryKey: superCategoryKeys.all });
    },
  });
}

export function useSystemSuperCategoryUpdate({ batch }: MutationOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SuperCategoryUpdatePayload }) =>
      mutator('PATCH', `/category-variant/super-categories/${id}`, payload),
    onSuccess: (_data, variables) => {
      if (batch) return;
      handleSuccess(null, { titleKey: 'success.superCategory.updated' });
      queryClient.invalidateQueries({ queryKey: superCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: superCategoryKeys.detail(variables.id) });
    },
  });
}

export function useSystemSuperCategoryDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      mutator('DELETE', `/category-variant/super-categories/${id}`),
    onSuccess: () => {
      handleSuccess(null, { titleKey: 'success.superCategory.deleted' });
      queryClient.invalidateQueries({ queryKey: superCategoryKeys.all });
    },
  });
}
